import mapboxgl from 'mapbox-gl';

import { MarkerManager } from './mapTypes';
import {
  calculateGimbalOffset,
  calculateGeographicBearing,
  adjustBearingForMapRotation,
  adjustGimbalBearingForMapRotation
} from './mapUtils';

export function updateDroneMarker(
  map: mapboxgl.Map | null,
  manager: MarkerManager,
  deviceId: string,
  position: [number, number],
  markerImage: HTMLImageElement | null,
  markerAngle: number | undefined,
  lines: [number, number][]
): void {
  if (!map || !markerImage || lines.length === 0) return;

  if (manager.shouldRecreateMarker(deviceId, markerImage)) {
    manager.removeMarker(deviceId);
  }

  if (!manager.hasMarker(deviceId)) {
    const mapBearing = map.getBearing();
    const rotation = markerAngle !== undefined ? adjustBearingForMapRotation(markerAngle, mapBearing) : undefined;

    manager.createMarker(deviceId, markerImage, position, map, rotation);
    return;
  }

  const marker = manager.getMarker(deviceId);
  if (!marker) return;

  let rotation: number | undefined;
  if (lines.length > 1) {
    const bearing =
      markerAngle !== undefined
        ? markerAngle
        : calculateGeographicBearing(lines[lines.length - 2], lines[lines.length - 1]);
    const mapBearing = map.getBearing();
    rotation = adjustBearingForMapRotation(bearing, mapBearing);
  }

  manager.updateMarker(deviceId, position, rotation);
}

export function updateGimbalMarker(
  map: mapboxgl.Map | null,
  manager: MarkerManager,
  deviceId: string,
  dronePosition: [number, number],
  gimbalImage: HTMLImageElement | null,
  gimbalAngle: number | undefined,
  lines: [number, number][],
  inPipMode: boolean,
  customPixelOffset?: number
): void {
  if (!map || !gimbalImage || lines.length === 0) return;

  const gimbalMarkerId = `${deviceId}_gimbal`;
  const angle = gimbalAngle ?? 0;
  const gimbalPosition = calculateGimbalOffset(dronePosition, angle, map, inPipMode, customPixelOffset);

  if (manager.shouldRecreateMarker(gimbalMarkerId, gimbalImage)) {
    manager.removeMarker(gimbalMarkerId);
  }

  if (!manager.hasMarker(gimbalMarkerId)) {
    const mapBearing = map.getBearing();
    const rotation = gimbalAngle !== undefined ? adjustGimbalBearingForMapRotation(gimbalAngle, mapBearing) : undefined;

    manager.createMarker(gimbalMarkerId, gimbalImage, gimbalPosition, map, rotation);
    return;
  }

  const mapBearing = map.getBearing();
  const rotation = gimbalAngle !== undefined ? adjustGimbalBearingForMapRotation(gimbalAngle, mapBearing) : undefined;

  manager.updateMarker(gimbalMarkerId, gimbalPosition, rotation);
}
